import React, { createContext, useState, useEffect } from 'react';
import { auth } from '../firebase/firebase.js';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const ADMIN_SESSION_KEY = 'remAdminAuthenticated';
const ADMIN_EMAILS = [
  'martinmoreiradg@gmail.com',
  'rementerprisesonline@gmail.com'
];

const getStoredAdminSession = () => {
  try {
    return sessionStorage.getItem(ADMIN_SESSION_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const storeAdminSession = (isAuthenticated) => {
  try {
    if (isAuthenticated) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, 'true');
    } else {
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    localStorage.removeItem('adminToken');
  } catch (error) {
    // Storage can fail in private browsing modes. Keep React state as source of truth.
  }
};

export const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  isAdminAuthenticated: false,
  setIsAdminAuthenticated: () => {},
  logout: () => {},
  adminLogout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdminAuthenticated, setAdminAuthenticatedState] = useState(getStoredAdminSession);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async fbUser => {
      if (fbUser) {
        setUser(fbUser);
        // forceRefresh = true
        const freshIdToken = await fbUser.getIdToken(true);
        setToken(freshIdToken);

        const hasStoredAdminSession = getStoredAdminSession();
        const isAuthorizedAdmin = ADMIN_EMAILS.includes(fbUser.email);
        if (!hasStoredAdminSession || !isAuthorizedAdmin) {
          storeAdminSession(false);
          setAdminAuthenticatedState(false);
        }
      } else {
        setUser(null);
        setToken(null);
        storeAdminSession(false);
        setAdminAuthenticatedState(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const setIsAdminAuthenticated = (nextValue) => {
    setAdminAuthenticatedState(prevValue => {
      const resolvedValue = typeof nextValue === 'function' ? nextValue(prevValue) : nextValue;
      storeAdminSession(Boolean(resolvedValue));
      return Boolean(resolvedValue);
    });
  };

  const logout = () => {
    storeAdminSession(false);
    setAdminAuthenticatedState(false);
    return signOut(auth);
  };

  const adminLogout = () => {
    storeAdminSession(false);
    setAdminAuthenticatedState(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAdminAuthenticated,
        setIsAdminAuthenticated,
        logout,
        adminLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
